import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, BlogPost as SupabaseBlogPost } from '../utils/supabase';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  author: string;
  date: string;
  status: 'draft' | 'published';
  imageUrl: string;
  tags: string[];
  linkedin_url?: string;
  createdAt: string;
  updatedAt: string;
}

interface BlogContextType {
  posts: BlogPost[];
  loading: boolean;
  error: string | null;
  refreshPosts: () => Promise<void>;
  getPostBySlug: (slug: string) => BlogPost | undefined;
}

const BlogContext = createContext<BlogContextType | undefined>(undefined);

const convertSupabaseToLocal = (post: SupabaseBlogPost): BlogPost => ({
  id: post.id,
  title: post.title || '',
  slug: post.slug || '',
  content: post.content || '',
  excerpt: post.excerpt || '',
  author: post.author || 'CTC Waterworks',
  date: post.date || new Date().toISOString().split('T')[0],
  status: post.status,
  imageUrl: post.image_url || '',
  tags: Array.isArray(post.tags) ? post.tags : [],
  linkedin_url: post.linkedin_url || '',
  createdAt: post.created_at || '',
  updatedAt: post.updated_at || ''
});

export const BlogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[BlogContext] Fetching posts with filters: status=published');

      const { data, error: fetchError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .order('date', { ascending: false });

      console.log('[BlogContext] Query result:', { data, error: fetchError });

      if (fetchError) {
        console.error('Error fetching blog posts:', fetchError);
        setError(fetchError.message || 'Failed to load blog posts');
        setPosts([]);
        return;
      }

      const convertedPosts = (data || []).map(convertSupabaseToLocal);
      console.log('[BlogContext] Converted posts:', convertedPosts);
      setPosts(convertedPosts);
    } catch (err: any) {
      console.error('Error fetching blog posts:', err);
      setError(err.message || 'Failed to load blog posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts().catch(err => {
      console.error('Failed to fetch posts on mount:', err);
      setLoading(false);
    });
  }, []);

  const refreshPosts = async () => {
    await fetchPosts();
  };

  const getPostBySlug = (slug: string): BlogPost | undefined => {
    return posts.find(post => post.slug === slug);
  };

  return (
    <BlogContext.Provider value={{
      posts,
      loading,
      error,
      refreshPosts,
      getPostBySlug
    }}>
      {children}
    </BlogContext.Provider>
  );
};

export const useBlog = () => {
  const context = useContext(BlogContext);
  if (context === undefined) {
    throw new Error('useBlog must be used within a BlogProvider');
  }
  return context;
};
